import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $createHeadingNode,
  $createQuoteNode,
  HeadingTagType,
} from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { message } from 'antd'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { useRolodexEdit } from 'features/fde'
import { INSERT_IMAGE_COMMAND } from 'features/richText'
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
} from 'lexical'
import { Image } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border2};
  background: transparent;
  border-radius: 0;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    gap: 0.2rem;
  }
`

const ToolbarButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.5rem;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.forecast.primary : 'transparent'};
  background: ${({ theme, $active }) =>
    $active ? theme.forecast.primary + '20' : 'transparent'};
  color: ${({ theme }) => theme.forecast.defaultText};
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 2rem;
  height: 2rem;

  @media (hover: hover) {
    &:hover {
      background: ${({ theme }) => theme.forecast.glassBackground};
      border-color: ${({ theme }) => theme.forecast.border2};
    }
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledDropdownWrapper = styled.div`
  min-width: 8rem;

  .rs-picker-default .rs-picker-toggle.rs-btn {
    font-size: 0.875rem;
    padding: 0.375rem 0.5rem;
    border: 1px solid ${({ theme }) => theme.forecast.border2};
    background: ${({ theme }) => theme.forecast.cardGroupBackground};
    color: ${({ theme }) => theme.forecast.defaultText};

    &:hover {
      background: ${({ theme }) => theme.forecast.glassBackground};
      border-color: ${({ theme }) => theme.forecast.border2};
    }

    &:focus {
      border-color: ${({ theme }) => theme.forecast.primary};
    }
  }

  .rs-picker-toggle-value {
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const ToolbarDivider = styled.div`
  width: 1px;
  height: 1.5rem;
  background: ${({ theme }) => theme.forecast.border2};
  margin: 0 0.25rem;
`

const ListIconWrapper = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: flex-start;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  width: 1.5rem;
`

const OrderedListRow = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  line-height: 1;
  width: 100%;
`

const NumberLabel = styled.span`
  width: 0.5rem;
  text-align: right;
  font-size: 0.6rem;
  font-weight: 600;
  display: flex;
  justify-content: flex-end;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-variant-numeric: tabular-nums;
`

const ListLine = styled.span`
  display: block;
  width: 1rem;
  height: 0.11rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.forecast.secondaryText};
  opacity: 0.9;
  flex: 1;
`

const BulletDot = styled.span`
  display: block;
  width: 0.22rem;
  height: 0.22rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.forecast.secondaryText};
`

const OrderedListIcon: React.FC = () => (
  <ListIconWrapper>
    <OrderedListRow>
      <NumberLabel>1</NumberLabel>
      <ListLine />
    </OrderedListRow>
    <OrderedListRow>
      <NumberLabel>2</NumberLabel>
      <ListLine />
    </OrderedListRow>
  </ListIconWrapper>
)

const UnorderedListIcon: React.FC = () => (
  <ListIconWrapper>
    <OrderedListRow>
      <BulletDot />
      <ListLine />
    </OrderedListRow>
    <OrderedListRow>
      <BulletDot />
      <ListLine />
    </OrderedListRow>
  </ListIconWrapper>
)

const HiddenFileInput = styled.input`
  display: none;
`

const BLOCK_TYPE_OPTIONS = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'quote', label: 'Quote' },
]

export interface ToolbarPluginProps {
  entryIdForImages?: string
}

export const ToolbarPlugin: React.FC<ToolbarPluginProps> = ({
  entryIdForImages,
}) => {
  const [editor] = useLexicalComposerContext()
  const { rolodexUploadImage } = useRolodexEdit()
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
  })
  const [blockType, setBlockType] = useState('paragraph')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      setActiveFormats({
        bold: selection.hasFormat('bold'),
        italic: selection.hasFormat('italic'),
        underline: selection.hasFormat('underline'),
        strikethrough: selection.hasFormat('strikethrough'),
      })
    }
  }, [])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar()
      })
    })
  }, [editor, updateToolbar])

  const formatText = (
    format: 'bold' | 'italic' | 'underline' | 'strikethrough'
  ) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatBlock = (type: string) => {
    if (type === 'paragraph') {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode())
        }
      })
    } else if (type === 'quote') {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode())
        }
      })
    } else if (type.startsWith('h')) {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () =>
            $createHeadingNode(type as HeadingTagType)
          )
        }
      })
    }
    setBlockType(type)
  }

  const insertList = (type: 'ul' | 'ol') => {
    if (type === 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    }
  }

  const handleImageButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file || !entryIdForImages || isUploading) {
      return
    }

    try {
      const maxSizeInMB = 10
      if (file.size > maxSizeInMB * 1024 * 1024) {
        message.error(`File must be smaller than ${maxSizeInMB}MB`)
        return
      }

      const acceptedFileTypes = ['.png', '.jpg', '.jpeg', '.gif', '.webp']
      const nameParts = file.name.split('.')
      let fileExtension = ''
      if (nameParts.length > 1) {
        fileExtension = `.${nameParts.pop()!.toLowerCase()}`
      }
      if (!acceptedFileTypes.includes(fileExtension)) {
        message.error(
          `File type not accepted. Please upload: ${acceptedFileTypes.join(', ')}`
        )
        return
      }

      setIsUploading(true)

      const timestamp = Date.now()
      const sanitizedFileName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '-')
        .toLowerCase()
      const imagePath = `images/${timestamp}-${sanitizedFileName}`

      const hideLoading = message.loading('Uploading image...', 0)

      try {
        await rolodexUploadImage(entryIdForImages, file, imagePath)

        hideLoading()
        message.success('Image uploaded successfully')

        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          entryId: entryIdForImages,
          imagePath,
          altText: file.name,
        })
      } catch (error) {
        hideLoading()
        console.error('Upload failed:', error)
        message.error('Failed to upload image')
      }
    } catch (error) {
      console.error('Image upload error:', error)
      message.error('An error occurred during upload')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <ToolbarContainer>
      {/* Block type selector */}
      <StyledDropdownWrapper>
        <Dropdown
          options={BLOCK_TYPE_OPTIONS}
          defaultValue={blockType}
          onValueChange={formatBlock}
          placeholder='Format'
          cleanable={false}
          searchable={false}
          mode='edit'
          $backgroundColor='transparent'
        />
      </StyledDropdownWrapper>

      <ToolbarDivider />

      {/* Text formatting */}
      <ToolbarButton
        $active={activeFormats.bold}
        onClick={() => formatText('bold')}
        title='Bold (Ctrl+B)'>
        <strong>B</strong>
      </ToolbarButton>

      <ToolbarButton
        $active={activeFormats.italic}
        onClick={() => formatText('italic')}
        title='Italic (Ctrl+I)'>
        <em>I</em>
      </ToolbarButton>

      <ToolbarButton
        $active={activeFormats.underline}
        onClick={() => formatText('underline')}
        title='Underline (Ctrl+U)'>
        <u>U</u>
      </ToolbarButton>

      <ToolbarButton
        $active={activeFormats.strikethrough}
        onClick={() => formatText('strikethrough')}
        title='Strikethrough'>
        <s>S</s>
      </ToolbarButton>

      {/* Lists */}
      <ToolbarButton onClick={() => insertList('ol')} title='Numbered List'>
        <OrderedListIcon />
      </ToolbarButton>

      <ToolbarButton onClick={() => insertList('ul')} title='Bullet List'>
        <UnorderedListIcon />
      </ToolbarButton>

      {/* Image Upload */}
      {entryIdForImages && (
        <>
          <ToolbarDivider />
          <ToolbarButton
            onClick={handleImageButtonClick}
            title={isUploading ? 'Uploading...' : 'Upload Image'}
            disabled={isUploading}>
            <Image size={20} />
          </ToolbarButton>
          <HiddenFileInput
            ref={fileInputRef}
            type='file'
            accept='.png,.jpg,.jpeg,.gif,.webp'
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </>
      )}
    </ToolbarContainer>
  )
}
